import { QueryClient, QueryKey } from '@tanstack/react-query';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from './query-client.constants';

export { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from './query-client.constants';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: DEFAULT_STALE_TIME,
            gcTime: DEFAULT_GC_TIME,
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

/** Reads a query imperatively outside React render, sharing the cache entry of the matching `usePocQuery`. */
export const fetchQueryCached = <T>(
    queryKey: QueryKey,
    queryFn: () => Promise<T>,
    options?: { staleTime?: number; retry?: number | false }
) => queryClient.fetchQuery({ queryKey, queryFn, ...options });

/** Cancels in-flight fetches matching the given key. */
export const cancelQuery = (queryKey: QueryKey) => queryClient.cancelQueries({ queryKey });

/** Writes the cache entry of the matching `usePocQuery` through an updater, returning the stored value. */
export const setQueryCache = <T>(queryKey: QueryKey, updater: (current: T | undefined) => T | undefined) =>
    queryClient.setQueryData<T>(queryKey, updater);

/** Invalidates queries matching the given key, triggering a background refetch for active subscribers. */
export const updateQuery = (queryKey: QueryKey) => queryClient.invalidateQueries({ queryKey });

/** Removes queries matching the given key from cache to avoid serving stale data on next read. */
export const removeQuery = (queryKey: QueryKey) => queryClient.removeQueries({ queryKey });

/** Removes all queries from cache and cancels any in-flight requests. */
export const clearCache = () => {
    queryClient.cancelQueries();
    queryClient.clear();
};
